import { QueueStrategy, TextToSpeech } from "@capacitor-community/text-to-speech";
import { Capacitor, registerPlugin } from "@capacitor/core";

export interface VoiceDescriptor {
  name: string;
  lang: string;
  voiceURI: string;
}

export interface PersianSpeechOptions {
  preferredVoiceUri?: string;
  rate?: number;
}

export type PersianSpeechOutcome = "spoken" | "setup_opened" | "unavailable";

let bundledAudio: HTMLAudioElement | undefined;

export async function playBundledPersianAudio(source: string): Promise<boolean> {
  if (typeof Audio === "undefined") {
    return false;
  }

  try {
    if (bundledAudio) {
      bundledAudio.pause();
      bundledAudio.currentTime = 0;
    }

    bundledAudio = new Audio(source);
    bundledAudio.preload = "auto";
    await bundledAudio.play();
    return true;
  } catch {
    return false;
  }
}

type NativePersianTtsStatus = "spoken" | "missing_language" | "unavailable" | "failed";

interface NativePersianTtsPlugin {
  speak(options: { text: string; rate?: number }): Promise<{ status: NativePersianTtsStatus }>;
  openSettings(): Promise<void>;
}

const NativePersianTts = registerPlugin<NativePersianTtsPlugin>("PersianTts");

export function selectPersianVoice<T extends VoiceDescriptor>(
  voices: readonly T[],
  preferredVoiceUri?: string,
): T | undefined {
  if (preferredVoiceUri) {
    const preferred = voices.find((voice) => voice.voiceURI === preferredVoiceUri);

    if (preferred) {
      return preferred;
    }
  }

  return voices.find((voice) => voice.lang.toLowerCase().startsWith("fa"));
}

export function findPersianLanguage(languages: readonly string[]): string | undefined {
  return (
    languages.find((language) => language.toLowerCase() === "fa-ir") ??
    languages.find((language) => language.toLowerCase().startsWith("fa"))
  );
}

export function selectPersianLanguage(languages: readonly string[]): string {
  return findPersianLanguage(languages) ?? "fa-IR";
}

export function persianLanguageCandidates(languages: readonly string[]): string[] {
  const reported = languages.filter((language) => language.toLowerCase().startsWith("fa"));
  const ordered = [
    languages.find((language) => language.toLowerCase() === "fa-ir"),
    ...reported,
    "fa-IR",
    "fa",
    "fa-AF",
  ].filter((language): language is string => Boolean(language));

  return [...new Set(ordered)];
}

function speakWithWebSpeech(text: string, options: PersianSpeechOptions): boolean {
  if (
    typeof window === "undefined" ||
    !("speechSynthesis" in window) ||
    typeof SpeechSynthesisUtterance === "undefined"
  ) {
    return false;
  }

  const utterance = new SpeechSynthesisUtterance(text);
  utterance.lang = "fa-IR";
  utterance.rate = options.rate ?? 1.08;

  const voice = selectPersianVoice(window.speechSynthesis.getVoices(), options.preferredVoiceUri);

  if (voice) {
    utterance.voice = voice;
  }

  window.speechSynthesis.cancel();
  window.speechSynthesis.speak(utterance);
  return true;
}

async function speakWithAndroidNativeTts(
  text: string,
  options: PersianSpeechOptions,
): Promise<boolean> {
  if (Capacitor.getPlatform() !== "android") {
    return false;
  }

  try {
    const result = await NativePersianTts.speak({
      text,
      rate: options.rate ?? 1.04,
    });
    return result.status === "spoken";
  } catch {
    return false;
  }
}

async function speakWithIosNativeTts(
  text: string,
  options: PersianSpeechOptions,
): Promise<boolean> {
  if (Capacitor.getPlatform() !== "ios" || !Capacitor.isPluginAvailable("TextToSpeech")) {
    return false;
  }

  const supported = await TextToSpeech.getSupportedLanguages().catch(() => ({ languages: [] }));
  const candidates = persianLanguageCandidates(supported.languages);

  for (const language of candidates) {
    try {
      await TextToSpeech.speak({
        text,
        lang: language,
        rate: options.rate ?? 1.04,
        pitch: 1,
        volume: 1,
        queueStrategy: QueueStrategy.Flush,
      });
      return true;
    } catch {
      // Try the next Persian locale.
    }
  }

  return false;
}

export async function openNativeTtsSetup(): Promise<boolean> {
  if (Capacitor.getPlatform() !== "android") {
    return false;
  }

  try {
    await NativePersianTts.openSettings();
    return true;
  } catch {
    try {
      if (Capacitor.isPluginAvailable("TextToSpeech")) {
        await TextToSpeech.openInstall();
        return true;
      }
    } catch {
      // Fall through to unavailable.
    }

    return false;
  }
}

export async function speakPersianAsync(
  text: string,
  options: PersianSpeechOptions = {},
): Promise<boolean> {
  if (await speakWithAndroidNativeTts(text, options)) {
    return true;
  }

  if (await speakWithIosNativeTts(text, options)) {
    return true;
  }

  return speakWithWebSpeech(text, options);
}

export async function speakPersianWithSetup(
  text: string,
  options: PersianSpeechOptions = {},
): Promise<PersianSpeechOutcome> {
  if (await speakPersianAsync(text, options)) {
    return "spoken";
  }

  if (await openNativeTtsSetup()) {
    return "setup_opened";
  }

  return "unavailable";
}

export function speakPersian(text: string, options: PersianSpeechOptions = {}): boolean {
  if (Capacitor.isNativePlatform()) {
    void speakPersianAsync(text, options);
    return true;
  }

  return speakWithWebSpeech(text, options);
}
